/**
 * WorldClock (N2b) — a slim, live world-clock strip for the key branch cities,
 * plus a per-user "your timezone" selector (the timezone control, reachable by
 * everyone since Settings is admin-only).
 *
 * Live: re-renders every second. DST-safe via lib/time (Intl). Responsive: on a
 * narrow window it collapses to the two anchors (Toronto + Vancouver). If the
 * user's own zone isn't among the cities, it's appended so travelling reps see
 * their local time too.
 */

import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useApiClient } from '../hooks/useApi';
import { clockInTz, userTz, tzCityLabel, TZ_OPTIONS } from '../lib/time';
import { Icons } from '../lib/icons';

const CITIES = [
  { label: 'Halifax', tz: 'America/Halifax' },
  { label: 'Toronto', tz: 'America/Toronto' },
  { label: 'Winnipeg', tz: 'America/Winnipeg' },
  { label: 'Calgary', tz: 'America/Edmonton' },
  { label: 'Vancouver', tz: 'America/Vancouver' },
];
const ANCHORS = new Set(['America/Toronto', 'America/Vancouver']);
const NARROW = 720;

export function WorldClock() {
  const { user, updateUser } = useAuth();
  const api = useApiClient();
  const tz = userTz(user);

  const [now, setNow] = useState(new Date());
  const [narrow, setNarrow] = useState(typeof window !== 'undefined' && window.innerWidth < NARROW);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    const onResize = () => setNarrow(window.innerWidth < NARROW);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Which cities to show: all five (or just the anchors when narrow), plus the
  // user's own zone if it isn't already represented.
  let cities = narrow ? CITIES.filter((c) => ANCHORS.has(c.tz)) : CITIES.slice();
  if (!cities.some((c) => c.tz === tz)) {
    cities = [...cities, { label: tzCityLabel(tz), tz }];
  }

  const changeTz = async (value: string) => {
    if (!user || value === tz) return;
    setSaving(true);
    updateUser({ timezone: value }); // optimistic — re-renders times immediately
    try {
      await api.updateUser(user.id, { timezone: value });
    } catch (e: any) {
      window.alert(e?.message || 'Could not save timezone');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', background: 'var(--crm-panel-2)', border: '1px solid var(--crm-line)', borderRadius: '10px', padding: '8px 12px', marginBottom: '14px' }}>
      <span style={{ display: 'inline-flex', color: 'var(--crm-muted)' }}><Icons.world size={16} aria-hidden="true" /></span>
      <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', flex: 1 }}>
        {cities.map((c) => {
          const { time, weekday } = clockInTz(c.tz, now);
          const isMine = c.tz === tz;
          return (
            <div key={c.tz} style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', minWidth: '64px' }}>
              <span style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '.4px', color: isMine ? 'var(--crm-accent)' : 'var(--crm-muted)', fontWeight: 700 }}>
                {c.label}{isMine ? ' • you' : ''}
              </span>
              <span style={{ fontSize: '14px', fontWeight: 700, color: 'var(--crm-text)', fontFamily: "'DM Mono', monospace" }}>
                {time}
                <span style={{ fontSize: '10px', color: 'var(--crm-dim)', marginLeft: '5px', fontFamily: 'inherit' }}>{weekday}</span>
              </span>
            </div>
          );
        })}
      </div>
      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: 'var(--crm-muted)' }}>
        <span style={{ whiteSpace: 'nowrap' }}>Your timezone</span>
        <select
          value={tz}
          disabled={saving}
          onChange={(e) => changeTz(e.target.value)}
          style={{ padding: '5px 8px', background: 'var(--crm-panel-2)', border: '1px solid var(--crm-line)', borderRadius: '6px', color: 'var(--crm-text)', fontSize: '11px', cursor: 'pointer' }}
        >
          {TZ_OPTIONS.some((o) => o.value === tz) ? null : <option value={tz}>{tzCityLabel(tz)}</option>}
          {TZ_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </label>
    </div>
  );
}
